import { useMemo, useReducer, useState } from 'react';
import type { IClaimFilterState, IClaim } from '@/@types/claims';

type FilterAction = // LOCAL_TYPE_JUSTIFIED: reducer action types used only in this hook
  | { type: 'SET_SEARCH'; query: string }
  | { type: 'SET_STATUS'; status: string }
  | { type: 'SET_TREATMENT_TYPE'; treatmentType: string }
  | { type: 'SET_DATE_RANGE'; dateRange: string }
  | { type: 'RESET' };

const initialFilterState: IClaimFilterState = {
  search: '',
  status: 'all',
  treatmentType: 'all',
  dateRange: 'all',
};

function filterReducer(state: IClaimFilterState, action: FilterAction): IClaimFilterState {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, search: action.query };
    case 'SET_STATUS':
      return { ...state, status: action.status as IClaimFilterState['status'] }; // AS_JUSTIFIED: status string from Select onValueChange narrowed to typed union
    case 'SET_TREATMENT_TYPE':
      return { ...state, treatmentType: action.treatmentType };
    case 'SET_DATE_RANGE':
      return { ...state, dateRange: action.dateRange as IClaimFilterState['dateRange'] }; // AS_JUSTIFIED: dateRange string from Select onValueChange narrowed to typed union
    case 'RESET':
      return initialFilterState;
  }
}

const DATE_RANGE_MS: Record<string, number> = {
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
};

export function useClaimFilters(claims: IClaim[]) {
  const [filters, dispatchFilter] = useReducer(filterReducer, initialFilterState);
  // Capture "now" once at mount — date-range cutoff is relative to when the hook first renders.
  // Re-mounts (e.g. page navigation) refresh it; within a session the window is acceptable.
  const [nowMs] = useState(() => Date.now());

  const filteredClaims = useMemo(() => {
    let result = [...claims];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(
        c =>
          c.claimNumber.toLowerCase().includes(q) ||
          (c.providerName?.toLowerCase().includes(q) ?? false) ||
          (c.diagnosis?.toLowerCase().includes(q) ?? false)
      );
    }

    if (filters.status !== 'all') {
      result = result.filter(c => c.status === filters.status);
    }

    if (filters.treatmentType !== 'all') {
      result = result.filter(c => c.treatmentType === filters.treatmentType);
    }

    if (filters.dateRange !== 'all') {
      const cutoff = nowMs - (DATE_RANGE_MS[filters.dateRange] ?? 0);
      result = result.filter(c => c.createdAt >= cutoff);
    }

    return [...result].sort((a, b) => b.createdAt - a.createdAt);
  }, [claims, filters.search, filters.status, filters.treatmentType, filters.dateRange, nowMs]);

  return { filters, filteredClaims, dispatchFilter };
}
